import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { MouseEvent as RMouseEvent } from 'react'
import ChatPanel from './ChatPanel'

export type DockMode = 'float' | 'left' | 'right'

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

const LS_MODE = 'aisheet.dock.mode'
const LS_WIDTH = 'aisheet.dock.width'
const LS_RECT = 'aisheet.dock.rect'
const MIN_W = 300
const MIN_H = 240

function loadMode(): DockMode {
  const v = localStorage.getItem(LS_MODE)
  return v === 'left' || v === 'right' || v === 'float' ? v : 'float'
}

function defaultRect(): Rect {
  const w = Math.min(420, Math.max(MIN_W, window.innerWidth - 80))
  const h = Math.min(600, Math.max(MIN_H, window.innerHeight - 140))
  return { x: Math.max(12, window.innerWidth - w - 28), y: Math.max(12, window.innerHeight - h - 40), w, h }
}

function loadRect(): Rect {
  try {
    const raw = localStorage.getItem(LS_RECT)
    if (!raw) return defaultRect()
    const r = JSON.parse(raw) as Rect
    if ([r.x, r.y, r.w, r.h].some((n) => typeof n !== 'number' || Number.isNaN(n))) return defaultRect()
    return r
  } catch {
    return defaultRect()
  }
}

function clampRect(r: Rect): Rect {
  const w = Math.min(Math.max(MIN_W, r.w), window.innerWidth - 16)
  const h = Math.min(Math.max(MIN_H, r.h), window.innerHeight - 16)
  return {
    w,
    h,
    x: Math.min(Math.max(8, r.x), Math.max(8, window.innerWidth - w - 8)),
    y: Math.min(Math.max(8, r.y), Math.max(8, window.innerHeight - h - 8)),
  }
}

/**
 * AI 패널의 배치 컨테이너.
 * 좌측 도킹 / 우측 도킹 / 플로팅 창 세 가지를 오가며, 선택은 localStorage 에 남는다.
 * 기본값은 플로팅.
 */
export default function ChatDock() {
  const [mode, setMode] = useState<DockMode>(() => loadMode())
  const [width, setWidth] = useState(() => Number(localStorage.getItem(LS_WIDTH)) || 400)
  const [rect, setRect] = useState<Rect>(() => loadRect())
  const [collapsed, setCollapsed] = useState(false)
  const [hidden, setHidden] = useState(false)

  const drag = useRef<{ kind: 'move' | 'size' | 'dock'; dx: number; dy: number; base: Rect } | null>(null)

  useEffect(() => localStorage.setItem(LS_MODE, mode), [mode])
  useEffect(() => localStorage.setItem(LS_WIDTH, String(width)), [width])
  useEffect(() => localStorage.setItem(LS_RECT, JSON.stringify(rect)), [rect])

  useLayoutEffect(() => {
    if (mode === 'float') setRect((r) => clampRect(r))
  }, [mode])

  useEffect(() => {
    const onResize = () => setRect((r) => clampRect(r))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = drag.current
      if (!d) return
      if (d.kind === 'dock') {
        const w = mode === 'right' ? window.innerWidth - e.clientX : e.clientX
        setWidth(Math.min(Math.max(MIN_W, w), window.innerWidth * 0.7))
        return
      }
      if (d.kind === 'move') {
        setRect(clampRect({ ...d.base, x: e.clientX - d.dx, y: e.clientY - d.dy }))
      } else {
        setRect(clampRect({ ...d.base, w: d.base.w + (e.clientX - d.dx), h: d.base.h + (e.clientY - d.dy) }))
      }
    }
    const onUp = () => {
      drag.current = null
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [mode])

  const beginDrag = useCallback(
    (kind: 'move' | 'size' | 'dock') => (e: RMouseEvent) => {
      if (kind !== 'dock' && (e.target as HTMLElement).closest('button, input, textarea, select')) return
      e.preventDefault()
      document.body.style.userSelect = 'none'
      drag.current =
        kind === 'move'
          ? { kind, dx: e.clientX - rect.x, dy: e.clientY - rect.y, base: rect }
          : { kind, dx: e.clientX, dy: e.clientY, base: rect }
    },
    [rect]
  )

  const controls = (
    <div className="dock-switch" onMouseDown={(e) => e.stopPropagation()}>
      {(
        [
          ['left', '◧', '왼쪽에 붙이기'],
          ['float', '⬔', '플로팅 창'],
          ['right', '◨', '오른쪽에 붙이기'],
        ] as Array<[DockMode, string, string]>
      ).map(([m, icon, tip]) => (
        <button
          key={m}
          className={`dock-btn${mode === m ? ' on' : ''}`}
          title={tip}
          onClick={() => {
            setMode(m)
            setHidden(false)
            setCollapsed(false)
          }}
        >
          {icon}
        </button>
      ))}
      {mode === 'float' && (
        <>
          <button className="dock-btn" title={collapsed ? '펼치기' : '접기'} onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? '▢' : '▁'}
          </button>
          <button className="dock-btn" title="닫기" onClick={() => setHidden(true)}>
            ✕
          </button>
        </>
      )}
    </div>
  )

  // 창 크기가 어떻게 변했든 화면 밖으로 나가지 않도록 그릴 때 한 번 더 조인다
  const view = mode === 'float' ? clampRect(rect) : rect

  if (mode === 'float') {
    if (hidden)
      return (
        <button className="chat-fab" title="AI 어시스턴트 열기" onClick={() => setHidden(false)}>
          🤖
        </button>
      )
    return (
      <div
        className={`chat-float${collapsed ? ' collapsed' : ''}`}
        style={{ left: view.x, top: view.y, width: view.w, height: collapsed ? undefined : view.h }}
      >
        <ChatPanel
          controls={controls}
          bodyHidden={collapsed}
          onHeaderMouseDown={beginDrag('move')}
          onHeaderDoubleClick={() => setCollapsed((c) => !c)}
        />
        {!collapsed && <div className="float-grip" onMouseDown={beginDrag('size')} title="크기 조절" />}
      </div>
    )
  }

  return (
    <aside className={`chat-side ${mode}`} style={{ width }}>
      <div className={`dock-resizer ${mode}`} onMouseDown={beginDrag('dock')} />
      <ChatPanel controls={controls} />
    </aside>
  )
}
